import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import type { TaskItemView } from '../api/generated/models/TaskItemView';
import EmptyState from '../components/EmptyState';
import TaskBoard from '../components/tasks/TaskBoard';
import TaskCard from '../components/tasks/TaskCard';
import TaskDetailModal from '../components/tasks/TaskDetailModal';
import TaskFormModal from '../components/tasks/TaskFormModal';
import TaskStatusSwitcher from '../components/tasks/TaskStatusSwitcher';
import robotTasksIcon from '../assets/icons/robot-tasks.svg';
import { usePermissions } from '../hooks/usePermissions';
import { AppDispatch, RootState } from '../store/store';
import {
  clearSelectedTask,
  fetchAssignableUsers,
  fetchTaskById,
  fetchTasks,
  patchTask,
} from '../store/slices/taskSlice';
import { addNotification } from '../store/slices/notificationSlice';
import {
  isTaskBoardStatus,
  TASK_BOARD_STATUSES,
  type TaskBoardStatus,
  toPatchStatus,
} from '../utils/taskHelpers';

const TasksPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    items,
    selectedTask,
    assignableUsers,
    detailLoading,
    updateLoading,
    isLoaded,
  } = useSelector((state: RootState) => state.tasks);

  // Subuser: task create / edit / delete / status changes require mobile_write.
  const { can } = usePermissions();
  const canWrite = can('mobile_write');

  const [mobileStatus, setMobileStatus] = useState<TaskBoardStatus>(TASK_BOARD_STATUSES[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formTask, setFormTask] = useState<TaskItemView | null>(null);

  useEffect(() => {
    dispatch(fetchTasks());
    dispatch(fetchAssignableUsers());
  }, [dispatch]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    const taskId = searchParams.get('taskId');
    if (!taskId) {
      return;
    }

    const openFromDeepLink = async () => {
      setIsModalOpen(true);
      try {
        await dispatch(fetchTaskById(taskId)).unwrap();
      } catch {
        setIsModalOpen(false);
        dispatch(clearSelectedTask());
        dispatch(addNotification({
          type: 'error',
          title: t('notification_error'),
          message: t('notifications.feed.task_not_found'),
        }));
      } finally {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('taskId');
          return next;
        }, { replace: true });
      }
    };

    void openFromDeepLink();
  }, [dispatch, isLoaded, searchParams, setSearchParams, t]);

  const boardItems = useMemo(
    () => items.filter((item) => isTaskBoardStatus(item.status)),
    [items]
  );

  const mobileTasks = useMemo(
    () => boardItems.filter((item) => item.status === mobileStatus),
    [boardItems, mobileStatus]
  );

  const handleTaskClick = useCallback(async (taskId: string) => {
    setIsModalOpen(true);
    await dispatch(fetchTaskById(taskId));
  }, [dispatch]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    dispatch(clearSelectedTask());
  }, [dispatch]);

  const handleStatusChange = useCallback(async (taskId: string, status: string) => {
    if (!canWrite) {
      return;
    }
    await dispatch(patchTask({
      taskId,
      status: toPatchStatus(status),
    })).unwrap();
  }, [dispatch, canWrite]);

  const handleOpenCreate = useCallback(() => {
    setFormTask(null);
    setIsFormOpen(true);
  }, []);

  const handleEditTask = useCallback((task: TaskItemView) => {
    setIsModalOpen(false);
    dispatch(clearSelectedTask());
    setFormTask(task);
    setIsFormOpen(true);
  }, [dispatch]);

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setFormTask(null);
  }, []);

  if (!isLoaded) {
    return null;
  }

  return (
    <div>
      <div className="tasks-content">
        <div className="tasks-header">
          <h1>{t('sidebar_tasks')}</h1>
          <button
            type="button"
            className="btn btn-primary tasks-add-btn"
            onClick={handleOpenCreate}
            disabled={!canWrite}
            title={!canWrite ? t('subuser_no_permission_tooltip') : undefined}
          >
            {t('tasks_add_task')}
            <span className="tasks-add-btn-plus" aria-hidden="true">+</span>
          </button>
        </div>

        {boardItems.length === 0 ? (
          <div className="table-container tasks-table-container">
            <EmptyState
              title={t('no_tasks_yet')}
              description={t('no_tasks_description')}
              illustrationSrc={robotTasksIcon}
              illustrationPosition="top"
              illustrationWidth={120}
            />
          </div>
        ) : (
          <div className="tasks-page-body">
            <div className="tasks-mobile-view">
              <TaskStatusSwitcher
                activeStatus={mobileStatus}
                onChange={setMobileStatus}
              />
              <section className="tasks-mobile-panel">
                <header className="tasks-column-header">
                  <h2 className="tasks-column-title">{t(`tasks_status_${mobileStatus}`)}</h2>
                  <span className="tasks-column-count">{mobileTasks.length}</span>
                </header>
                <div className="tasks-column-body">
                  {mobileTasks.map((task) => (
                    <TaskCard key={task.id} task={task} onClick={handleTaskClick} />
                  ))}
                </div>
              </section>
            </div>

            <TaskBoard
              items={boardItems}
              onTaskClick={handleTaskClick}
              onTaskStatusChange={handleStatusChange}
              canDrag={canWrite}
            />
          </div>
        )}
      </div>

      <TaskDetailModal
        isOpen={isModalOpen}
        task={selectedTask}
        loading={detailLoading}
        updating={updateLoading}
        onClose={handleCloseModal}
        onStatusChange={handleStatusChange}
        onEdit={handleEditTask}
        canWrite={canWrite}
      />

      <TaskFormModal
        isOpen={isFormOpen}
        task={formTask}
        assignableUsers={assignableUsers}
        canAssign={canWrite}
        onClose={handleCloseForm}
      />
    </div>
  );
};

export default TasksPage;
